import React, { useEffect, useMemo, useState } from 'react';
import { Box, Stack, Group, Text, Divider, Button, Badge, Tooltip, Paper, SimpleGrid, Chip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconX, IconLock, IconSend, IconArrowBackUp } from '@tabler/icons-react';
import { ManualTrade } from '../../types';

export type ManualTradeViewProps = {
    trade: ManualTrade;
    onSendToGeneva: (trade: ManualTrade, isUndo?: boolean) => Promise<void>;
    onStateChange?: (state: NonNullable<ManualTrade['state']>) => void;
    disableSendIfLocked?: boolean; // default true
};

type ViewState = NonNullable<ManualTrade['state']>;

// Small UI helpers
const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
    <Group justify="space-between" wrap="nowrap">
        <Text c="dimmed" size="sm">{label}</Text>
        <Text size="sm" ta="right" style={{ maxWidth: 420, wordBreak: 'break-word' }}>
            {value !== undefined && value !== null && value !== '' ? value : <Text c="dimmed" size="sm">N/A</Text>}
        </Text>
    </Group>
);

export const InfoSection: React.FC<{ title: string; children: React.ReactNode; cols?: number }> = ({ title, children, cols = 2 }) => (
    <Box>
        <Divider my="xs" label={<Text fw={500}>{title}</Text>} />
        <SimpleGrid cols={{ base: 1, md: cols }} spacing="xs" p="xs">
            {children}
        </SimpleGrid>
    </Box>
);

// Formatting helpers
const fmtNum = (n?: number, frac: number = 4) =>
    n === undefined || n === null ? '' : new Intl.NumberFormat('en-US', { maximumFractionDigits: frac }).format(n);

const toDate = (ts: unknown): Date | null => {
    if (!ts) return null;
    const t: any = ts as any;
    if (typeof t.toDate === 'function') return t.toDate(); // Firestore Timestamp
    if (typeof t._seconds === 'number') return new Date(t._seconds * 1000); // raw
    return null;
};

const fmtDate = (ts: unknown) => {
    const d = toDate(ts);
    if (!d) return '';
    return d.toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' });
};

const stateColor = (s?: ViewState) => {
    switch (s) {
        case 'LOCKED':
            return 'teal';
        case 'GENEVA_ERROR':
            return 'red';
        default:
            return 'gray';
    }
};


const BUY_BADGE = <Badge color="green">BUY</Badge>;
const SELL_BADGE = <Badge color="red">SELL</Badge>;

const EXISTING_BADGE = <Badge color="gray" variant="light">Existing</Badge>;
const NEW_BADGE = <Badge color="blue" variant="filled">New</Badge>;

// Main component
const ManualTradeView: React.FC<ManualTradeViewProps> = ({ trade, onSendToGeneva, onStateChange, disableSendIfLocked = true }) => {
    const [state, setState] = useState<ViewState>(trade.state ?? 'PENDING');
    const [sending, setSending] = useState(false);
    const [undoing, setUndoing] = useState(false);

    useEffect(() => setState(trade.state ?? 'PENDING'), [trade.state]);

    const isSukuk = trade.type === 'sukuk';
    const isMoneyMarket = trade.type === 'moneyMarket';
    const isBuy = trade.transactionType === 'BUY';
    const isSell = trade.transactionType === 'SELL';

    const caseLabel = useMemo(() => {
        if (isSukuk) {
            if (isBuy) return trade.selectedSukukId ? 'Buy Existing Sukuk' : 'Create Sukuk and Buy';
            return 'Sell Existing Sukuk';
        }
        return 'Create Money Market and Buy';
    }, [isSukuk, isBuy, trade.selectedSukukId]);

    // Create vs existing security flags
    const isNewSecurity = isSukuk ? !trade.selectedSukukId : true; // MM is always create+buy
    const typeLabel = isSukuk ? 'Sukuk' : 'Money Market';
    const createLabel = isNewSecurity ? `Creates New ${typeLabel}` : `Uses Existing ${typeLabel}`;

    const handleSend = async () => {
        setSending(true);
        try {
            await onSendToGeneva(trade);
        } catch (err: any) {
            console.error('Geneva save failed:', err);
        } finally {
            setSending(false);
        }
    };
    const handleUndo = async () => {
        setUndoing(true);
        try {
            await onSendToGeneva(trade, true);
        } catch (err: any) {
            console.error('Geneva save failed:', err);
        } finally {
            setUndoing(false);
        }
    };

    return (
        <Stack gap="xs">
            <Paper withBorder p="sm" radius="md">
                <Group justify="space-between" align="center">
                    <Group gap="xs" align="center">
                        <Text fw={600}>Manual Trade</Text>
                        <Badge color={stateColor(state)} leftSection={state === 'LOCKED' ? <IconLock size={14} /> : undefined}>
                            {state}
                        </Badge>
                        {/* <Badge color={isSukuk ? 'grape' : 'indigo'}>{typeLabel}</Badge> */}
                        <Badge color={isNewSecurity ? 'teal' : 'gray'} variant={isNewSecurity ? 'filled' : 'light'}>
                            {createLabel}
                        </Badge>
                    </Group>
                    <Group gap="xs" align="center">
                        <Tooltip label={state === 'LOCKED' ? 'Already locked' : 'Send and lock this trade in Geneva'}>
                            <Button leftSection={<IconSend size={16} />} onClick={handleSend} loading={sending} disabled={disableSendIfLocked && state === 'LOCKED'}>
                                Send to Geneva
                            </Button>
                        </Tooltip>
                        <Tooltip label={state === 'LOCKED' ? 'Remove this trade from Geneva' : 'Cannot undo this action'}>
                            <Button leftSection={<IconArrowBackUp size={16} />} onClick={handleUndo} loading={undoing} disabled={state !== 'LOCKED'}>
                                Undo
                            </Button>
                        </Tooltip>
                    </Group>
                </Group>
            </Paper>

            {/* Sections: each takes full width; fields inside arranged into columns */}
            <InfoSection title="Trade" cols={2}>
                <Field label="ID" value={trade.id} />
                <Field label="Type" value={<Badge color={isSukuk ? 'grape' : 'indigo'}>{typeLabel}</Badge>} />
                {/* <Box>
                    <Text c="dimmed" size="sm">Transaction</Text>
                    <Group mt={4} gap="xs">
                        <Chip color="green" checked={isBuy} readOnly>BUY</Chip>
                        <Chip color="red" checked={isSell} readOnly>SELL</Chip>
                    </Group>
                </Box> */}
                <Field label="Transaction" value={isBuy ? BUY_BADGE : SELL_BADGE} />
                <Field label="Case" value={caseLabel} />
                <Field label="Portfolio" value={trade.portfolioId} />
                <Field label="Fund Structure" value={trade.fundStructure} />
                {/* <Field label="Security Creation" value={isNewSecurity ? 'Create new' : 'Use existing'} /> */}
            </InfoSection>

            <InfoSection title="Security" cols={2}>
                {isSukuk ? (
                    <>
                        {/* When buying/selling existing sukuk, selectedSukukId holds CODE; show code & desc clearly */}
                        {trade.selectedSukukId && (
                            <>
                                <Field label="Status" value={EXISTING_BADGE} />
                                <Field label="Code" value={trade.selectedSukukId} />
                            </>
                        )}
                        {!trade.selectedSukukId && (
                            <>
                                <Field label="Status" value={NEW_BADGE} />
                            </>
                        )}
                        {/* Always show sukuk details; description comes from inside sukuk */}
                        {trade.sukuk && (
                            <>
                                {/* <Field label="Code" value={trade.sukuk.CODE} /> */}
                                <Field label="Description" value={trade.sukuk.DESCRIPTION} />
                                <Field label="Coupon" value={
                                    trade.sukuk.COUPON !== undefined && trade.sukuk.COUPON !== null
                                        ? `${fmtNum(trade.sukuk.COUPON, 2)} %`
                                        : undefined
                                } />
                                <Field label="Coupon Freq" value={trade.sukuk.COUPON_FREQUENCY} />
                                <Field label="Maturity Date" value={fmtDate(trade.sukuk.MATURITYDATE)} />
                                <Field label="Issue Date" value={fmtDate(trade.sukuk.ISSUEDATE)} />
                                <Field label="ISIN" value={trade.sukuk.ISIN} />
                                <Field label="Issuer" value={trade.sukuk._ISSUER_NAMESORT_} />
                                <Field label="Bloomberg ID" value={trade.sukuk.BLOOMBERGID} />
                                <Field label="Currency" value={trade.sukuk._PRINCIPAL_CURRENCY_CODE_} />
                            </>
                        )}
                    </>
                ) : (
                    <>
                        <Field label="Issuer" value={trade.moneyMarket?.issuer} />
                        <Field label="Issuer Code" value={trade.moneyMarket?.issuerCode} />
                        <Field label="Description" value={trade.moneyMarket?.description} />
                        <Field label="Exchange" value={trade.moneyMarket?.exchange} />
                        <Field label="Rate" value={fmtNum(trade.moneyMarket?.rate, 4)} />
                        <Field label="Days" value={trade.moneyMarket?.days} />
                        <Field label="Maturity Date" value={fmtDate(trade.moneyMarket?.maturityDate)} />
                    </>
                )}
            </InfoSection>

            <InfoSection title="Dates" cols={2}>
                <Field label="Trade Date" value={fmtDate(trade.tradeDate)} />
                <Field label="Settlement Date" value={fmtDate(trade.settlementDate)} />
            </InfoSection>

            <InfoSection title="Amounts" cols={2}>
                <Field label="Nominal Value" value={fmtNum(trade.nominalValue, 2)} />
                <Field label="Price / Unit" value={fmtNum(trade.pricePerUnit, 6)} />
                <Field label="Total Price" value={fmtNum(trade.totalPrice, 2)} />
                <Field label="KFHC Fees" value={fmtNum(trade.kfhcFees, 2)} />
                <Field label="Principal" value={fmtNum(trade.principal, 2)} />
                <Field label="Accrued Profit" value={fmtNum(trade.accruedProfit, 2)} />
                <Field label="Settlement Amount" value={fmtNum(trade.settlementAmount, 2)} />
                <Field label="Yield" value={
                    trade.yield !== undefined && trade.yield !== null
                        ? `${fmtNum(trade.yield, 2)} %`
                        : undefined
                } />
            </InfoSection>

            <InfoSection title="Parties & Notes" cols={2}>
                <Field label="Counterparty" value={trade.counterpartyName} />
                <Field label="Counterparty Account" value={trade.counterpartyAccount} />
                <Field label="Custodian Account" value={trade.custodianAccountId} />
                <Field label="Remarks" value={trade.remarks} />
            </InfoSection>

            <InfoSection title="Audit" cols={2}>
                <Field label="Created By" value={trade.createdBy} />
                <Field label="Created At" value={fmtDate(trade.createdAt)} />
                <Field label="Updated By" value={trade.updatedBy} />
                <Field label="Updated At" value={fmtDate(trade.updatedAt)} />
            </InfoSection>
        </Stack>
    );
};

export default ManualTradeView;
